import { JSX, ReactNode, useContext } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import { KibSpinningLoader } from '@chewy/kib-overlays-react';

import { Header } from '@src/components/header';
import { LegalText } from '@src/components/legal';
import { Footer, CTA } from '@src/components/footer';
import { ErrorFallbackPage } from '@src/components/error-fallback/error-fallback-page';
import { SpinnerContext } from '@src/contexts/spinner-context';
import { useDynatrace } from '@src/hooks/use-dynatrace.hook';
import WindowService from '@src/services/window.service';

import '@src/components/layout/normalize.scss';
import '@chewy/kib-messaging-styles/dist/styles/kib-alert.css';
import '@chewy/kib-interstitials-styles/dist/styles/kib-spinner.css';
import '@chewy/kib-overlays-styles/dist/styles/kib-spinning-loader.css';
import '@src/components/layout/index.scss';

interface Props {
  id?: string;
  children: ReactNode;
  onBackClicked?: () => void;
  primaryCTA?: CTA;
  progress?: number;
  secondaryCTA?: CTA;
  legalText?: 'PRIVACY' | 'AFFIDAVIT';
  stickyFooter?: boolean;
  legalLast?: boolean;
  showLegal?: boolean;
  showFooterLegal?: boolean;
  fullWidth?: boolean;
  hideBackButton?: boolean;
}

const UnsafeLayoutContainer = ({
  id,
  children,
  onBackClicked,
  primaryCTA,
  secondaryCTA,
  legalText,
  stickyFooter = false,
  legalLast = false,
  showLegal = true,
  showFooterLegal = true,
  fullWidth = false,
  hideBackButton = false,
}: Props): JSX.Element => {
  const { isSpinnerVisible } = useContext(SpinnerContext);
  const { addAction } = useDynatrace();

  let baseClass = 'hc-pet-insurance__container';
  const legalClass = 'hc-pet-insurance__legal';

  const handleBackClicked = () => {
    addAction('Header Back Button Clicked');
    if (onBackClicked) {
      onBackClicked();
    } else {
      WindowService.goBack();
    }
  };

  if (fullWidth) {
    baseClass = `${baseClass} fullWidth`;
  }

  return (
    <>
      {isSpinnerVisible && <KibSpinningLoader emphasis />}
      <div
        id={id}
        className={stickyFooter ? `${baseClass} pi-footer--fixed` : baseClass}
        aria-busy={isSpinnerVisible}
        aria-hidden={isSpinnerVisible}
        data-testid="main-content-wrapper"
      >
        <Header
          {...(hideBackButton
            ? { hideBackButton: true }
            : { onBackClicked: handleBackClicked })}
        />
        <main
          className={
            fullWidth
              ? 'hc-pet-insurance__content fullWidth'
              : 'hc-pet-insurance__content'
          }
          data-testid="main-content"
          aria-label="content"
        >
          {children}
          {stickyFooter && showLegal && (
            <LegalText prefix={legalText} className={legalClass} />
          )}
        </main>
        {(primaryCTA || showLegal) && (
          // If there is no primaryCTA or showLegal, there is nothing to include in the footer - do not render it.
          <footer>
            {!stickyFooter && showLegal && !legalLast && (
              <LegalText prefix={legalText} className={legalClass} />
            )}
            {primaryCTA && (
              <Footer
                primaryCTA={primaryCTA}
                secondaryCTA={secondaryCTA}
                legalText={legalText}
                showFooterLegal={showFooterLegal}
              ></Footer>
            )}
            {!stickyFooter && showLegal && legalLast && (
              <LegalText prefix={legalText} className={legalClass} />
            )}
          </footer>
        )}
      </div>
    </>
  );
};

export const LayoutContainer = ({ children, ...props }: Props) => (
  <ErrorBoundary FallbackComponent={ErrorFallbackPage}>
    <UnsafeLayoutContainer {...props}>{children}</UnsafeLayoutContainer>
  </ErrorBoundary>
);
